import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import StatCard from '../components/StatCard';
import { api, fmtEUR } from '../lib/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';

const years = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 2 + i);

export default function Rentabilite() {
  const [annee, setAnnee] = useState(new Date().getFullYear());
  const [annuelle, setAnnuelle] = useState(null);
  const [mensuelle, setMensuelle] = useState([]);

  useEffect(() => {
    (async () => {
      const [a, m] = await Promise.all([
        api.get(`/dashboard/rentabilite-annuelle?annee=${annee}`),
        api.get(`/dashboard/rentabilite-mensuelle?annee=${annee}`),
      ]);
      setAnnuelle(a.data);
      setMensuelle(m.data.data);
    })();
  }, [annee]);

  return (
    <Layout
      title="Rentabilité"
      subtitle={`Exercice ${annee}`}
      actions={
        <Select value={String(annee)} onValueChange={(v) => setAnnee(Number(v))}>
          <SelectTrigger className="w-32 rounded-none border-zinc-900" data-testid="rentabilite-annee"><SelectValue /></SelectTrigger>
          <SelectContent className="rounded-none">
            {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
      }
    >
      <Tabs defaultValue="annuelle" className="w-full">
        <TabsList className="rounded-none bg-transparent border-b border-zinc-200 w-full justify-start h-auto p-0">
          <TabsTrigger value="annuelle" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-zinc-900 data-[state=active]:shadow-none px-6 py-3" data-testid="tab-annuelle">Annuelle</TabsTrigger>
          <TabsTrigger value="mensuelle" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-zinc-900 data-[state=active]:shadow-none px-6 py-3" data-testid="tab-mensuelle">Mensuelle</TabsTrigger>
        </TabsList>

        <TabsContent value="annuelle" className="mt-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <StatCard testid="rent-ca-estime" label="CA Estimé" value={annuelle?.ca_estime || 0} />
            <StatCard testid="rent-ca-realise" label="CA Réalisé" value={annuelle?.ca_realise || 0} delta={annuelle?.ca_ecart} deltaLabel="écart" accent />
            <StatCard testid="rent-charges-est" label="Charges Estimées" value={annuelle?.charges_estimees || 0} />
            <StatCard testid="rent-charges-real" label="Charges Réalisées" value={annuelle?.charges_realisees || 0} delta={annuelle?.charges_ecart} deltaLabel="écart" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white border border-zinc-200 border-l-4 border-l-[#002FA7] p-6">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Résultat estimé</div>
              <div className="font-mono font-medium text-4xl tracking-tight">{fmtEUR(annuelle?.resultat_estime || 0)}</div>
            </div>
            <div className="bg-white border border-zinc-200 border-l-4 border-l-[#002FA7] p-6">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Résultat réalisé</div>
              <div className={`font-mono font-medium text-4xl tracking-tight ${(annuelle?.resultat_realise || 0) >= 0 ? 'text-emerald-700' : 'text-rose-600'}`}>
                {fmtEUR(annuelle?.resultat_realise || 0)}
              </div>
            </div>
          </div>

          <div className="bg-white border border-zinc-200 p-6">
            <div className="flex items-baseline justify-between mb-6">
              <h3 className="font-display font-bold text-xl tracking-tight">Récapitulatif annuel</h3>
            </div>
            <table className="w-full text-sm">
              <thead className="border-b border-zinc-900">
                <tr className="text-left">
                  <th className="py-3 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Poste</th>
                  <th className="py-3 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium text-right">Estimé</th>
                  <th className="py-3 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium text-right">Réalisé</th>
                  <th className="py-3 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium text-right">Écart</th>
                  <th className="py-3 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium text-right">%</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-zinc-200">
                  <td className="py-3 font-medium">Chiffre d'affaires</td>
                  <td className="py-3 font-mono text-right">{fmtEUR(annuelle?.ca_estime || 0)}</td>
                  <td className="py-3 font-mono text-right">{fmtEUR(annuelle?.ca_realise || 0)}</td>
                  <td className={`py-3 font-mono text-right ${(annuelle?.ca_ecart || 0) >= 0 ? 'text-emerald-700' : 'text-rose-600'}`}>{fmtEUR(annuelle?.ca_ecart || 0)}</td>
                  <td className="py-3 font-mono text-right text-zinc-600">{annuelle?.ca_estime ? `${((annuelle.ca_realise / annuelle.ca_estime) * 100).toFixed(1)}%` : '—'}</td>
                </tr>
                <tr className="border-b border-zinc-200">
                  <td className="py-3 font-medium">Charges</td>
                  <td className="py-3 font-mono text-right">{fmtEUR(annuelle?.charges_estimees || 0)}</td>
                  <td className="py-3 font-mono text-right">{fmtEUR(annuelle?.charges_realisees || 0)}</td>
                  <td className={`py-3 font-mono text-right ${(annuelle?.charges_ecart || 0) <= 0 ? 'text-emerald-700' : 'text-rose-600'}`}>{fmtEUR(annuelle?.charges_ecart || 0)}</td>
                  <td className="py-3 font-mono text-right text-zinc-600">{annuelle?.charges_estimees ? `${((annuelle.charges_realisees / annuelle.charges_estimees) * 100).toFixed(1)}%` : '—'}</td>
                </tr>
                <tr className="border-t-2 border-zinc-900 font-semibold">
                  <td className="py-3 font-medium">Résultat</td>
                  <td className="py-3 font-mono text-right">{fmtEUR(annuelle?.resultat_estime || 0)}</td>
                  <td className={`py-3 font-mono text-right ${(annuelle?.resultat_realise || 0) >= 0 ? 'text-emerald-700' : 'text-rose-600'}`}>{fmtEUR(annuelle?.resultat_realise || 0)}</td>
                  <td className="py-3 font-mono text-right">—</td>
                  <td className="py-3 font-mono text-right">—</td>
                </tr>
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="mensuelle" className="mt-6 space-y-6">
          <div className="bg-white border border-zinc-200 p-6">
            <h3 className="font-display font-bold text-xl tracking-tight mb-4">CA Estimé vs Réalisé</h3>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={mensuelle} barCategoryGap="20%">
                <CartesianGrid strokeDasharray="0" stroke="#e4e4e7" vertical={false} />
                <XAxis dataKey="mois" tick={{ fontSize: 11, fontFamily: 'IBM Plex Mono' }} stroke="#71717a" />
                <YAxis tick={{ fontSize: 10, fontFamily: 'IBM Plex Mono' }} stroke="#71717a" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip contentStyle={{ borderRadius: 0, border: '1px solid #18181b', fontFamily: 'IBM Plex Mono', fontSize: 12 }} formatter={(v) => fmtEUR(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="ca_estime" fill="#A1A1AA" name="CA Estimé" />
                <Bar dataKey="ca_realise" fill="#002FA7" name="CA Réalisé" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white border border-zinc-200 p-6">
            <h3 className="font-display font-bold text-xl tracking-tight mb-4">Charges Estimées vs Réalisées</h3>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={mensuelle} barCategoryGap="20%">
                <CartesianGrid strokeDasharray="0" stroke="#e4e4e7" vertical={false} />
                <XAxis dataKey="mois" tick={{ fontSize: 11, fontFamily: 'IBM Plex Mono' }} stroke="#71717a" />
                <YAxis tick={{ fontSize: 10, fontFamily: 'IBM Plex Mono' }} stroke="#71717a" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip contentStyle={{ borderRadius: 0, border: '1px solid #18181b', fontFamily: 'IBM Plex Mono', fontSize: 12 }} formatter={(v) => fmtEUR(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="charges_estimees" fill="#D4D4D8" name="Charges Estimées" />
                <Bar dataKey="charges_realisees" fill="#E11D48" name="Charges Réalisées" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white border border-zinc-200 p-6">
            <h3 className="font-display font-bold text-xl tracking-tight mb-4">Résultat mensuel</h3>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={mensuelle}>
                <CartesianGrid strokeDasharray="0" stroke="#e4e4e7" vertical={false} />
                <XAxis dataKey="mois" tick={{ fontSize: 11, fontFamily: 'IBM Plex Mono' }} stroke="#71717a" />
                <YAxis tick={{ fontSize: 10, fontFamily: 'IBM Plex Mono' }} stroke="#71717a" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip contentStyle={{ borderRadius: 0, border: '1px solid #18181b', fontFamily: 'IBM Plex Mono', fontSize: 12 }} formatter={(v) => fmtEUR(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="linear" dataKey="resultat_estime" stroke="#A1A1AA" strokeWidth={2} name="Estimé" dot={{ r: 3 }} />
                <Line type="linear" dataKey="resultat_realise" stroke="#002FA7" strokeWidth={2} name="Réalisé" dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </TabsContent>
      </Tabs>
    </Layout>
  );
}
